import React from "react";

/**
 * Professional Salary Slip / Payroll Receipt
 *
 * Usage in Payroll page:
 *
 * <ReciptPrint
 *   open={openPrint}
 *   data={printData}
 *   onClose={() => {
 *     setOpenPrint(false);
 *     setPrintData(null);
 *   }}
 * />
 *
 * This component is designed to work with your existing payrollSalaryData row.
 */

const ReciptPrint = ({ open, data, onClose }) => {
  if (!open || !data) return null;

  const storeInfo = data?.store_info || {};

  const formatAmount = (value) => {
    const number = Number(value || 0);
    return number.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    } catch {
      return value;
    }
  };

  const safe = (value, fallback = "-") =>
    value !== undefined && value !== null && value !== "" ? value : fallback;

  const grossSalary = Number(data?.total_amount || data?.net_salary || 0);
  const taxAmount = Number(data?.tax_amount || 0);
  const netPay = Number(data?.total_amount || data?.net_salary || grossSalary);
  const salaryAmount = Number(data?.salary_amount || 0);
  const totalHours = Number(data?.total_hour || 0);
  const isBiometric = data?.is_bio === true || data?.salary_source === "Biometric";

  const earningsRows = [
    {
      label:
        data?.salary_unit === "hourly"
          ? "Hourly Pay"
          : data?.salary_unit === "weekly"
            ? "Weekly Salary"
            : data?.salary_unit === "bi weekly"
              ? "Bi-Weekly Salary"
              : data?.salary_unit === "monthly"
                ? "Monthly Salary"
                : "Basic Salary",
      basis:
        data?.salary_unit === "hourly"
          ? `${formatAmount(salaryAmount)} × ${formatAmount(totalHours)} hrs`
          : `${formatAmount(salaryAmount)} ${safe(data?.salary_unit, "")}`,
      amount: grossSalary + taxAmount,
    },
  ];

  const deductionRows = [
    {
      label:
        data?.tax_deduction_type === "percentage"
          ? `Tax Deduction ${data?.tax_percentage ? `(${data.tax_percentage}%)` : ""}`
          : "Tax Deduction",
      basis: data?.tax_deduction_type || "-",
      amount: taxAmount,
    },
  ].filter((row) => Number(row.amount || 0) > 0);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="salary-slip-modal-overlay">
      <div className="salary-slip-modal">
        <div className="salary-slip-toolbar no-print">
          <div>
            <h3>Salary Slip Preview</h3>
            <p>Review the details before printing.</p>
          </div>

          <div className="salary-slip-toolbar-actions">
            <button type="button" className="salary-slip-btn light" onClick={onClose}>
              Close
            </button>
            <button type="button" className="salary-slip-btn dark" onClick={handlePrint}>
              Print Salary Slip
            </button>
          </div>
        </div>

        <div className="salary-slip-print-area" id="salary-slip-print-area">
          <div className="salary-slip-document">
            <div className="salary-slip-top-band" />

            <div className="salary-slip-header">
              <div className="salary-slip-company">
                {storeInfo?.store_logo || storeInfo?.logo ? (
                  <img
                    src={storeInfo?.store_logo || storeInfo?.logo}
                    alt="Store Logo"
                    className="salary-slip-logo"
                  />
                ) : (
                  <div className="salary-slip-logo-placeholder">
                    {(storeInfo?.store_name || storeInfo?.name || "PV")
                      .slice(0, 2)
                      .toUpperCase()}
                  </div>
                )}

                <div>
                  <h1>{safe(storeInfo?.store_name || storeInfo?.name, "Company / Store Name")}</h1>
                  <p>
                    {[
                      storeInfo?.store_address_line1 || storeInfo?.address,
                      storeInfo?.store_city || storeInfo?.city,
                      storeInfo?.store_state || storeInfo?.state,
                      storeInfo?.store_pincode || storeInfo?.pincode,
                      storeInfo?.store_country || storeInfo?.country,
                    ]
                      .filter(Boolean)
                      .join(", ") || "Store address"}
                  </p>
                  {(storeInfo?.store_email || storeInfo?.email || storeInfo?.store_phone || storeInfo?.phone) && (
                    <p>
                      {[
                        storeInfo?.store_email || storeInfo?.email,
                        storeInfo?.store_phone || storeInfo?.phone,
                      ]
                        .filter(Boolean)
                        .join(" | ")}
                    </p>
                  )}
                </div>
              </div>

              <div className="salary-slip-title-box">
                <div className="salary-slip-title">SALARY SLIP</div>
                <div className="salary-slip-subtitle">
                  {isBiometric ? "Biometric Attendance Payroll" : "Manual Payroll"}
                </div>
                <div className="salary-slip-id">
                  Slip No: #{safe(data?.id || data?.salary_id || data?.payroll_id, "AUTO")}
                </div>
              </div>
            </div>

            <div className="salary-slip-summary-row">
              <div>
                <span>Employee</span>
                <strong>{safe(data?.payroll_employee_name || data?.employee_name || data?.full_name)}</strong>
              </div>
              <div>
                <span>Payroll Date</span>
                <strong>{formatDate(data?.payroll_date || data?.payment_date || data?.date)}</strong>
              </div>
              <div>
                <span>Pay Period</span>
                <strong>{formatDate(data?.start_date)} - {formatDate(data?.end_date)}</strong>
              </div>
              <div>
                <span>Net Pay</span>
                <strong className="salary-slip-net-highlight">$ {formatAmount(netPay)}</strong>
              </div>
            </div>

            <div className="salary-slip-section-grid">
              <div className="salary-slip-card">
                <div className="salary-slip-card-title">Employee Details</div>
                <div className="salary-slip-info-table">
                  <div><span>Name</span><b>{safe(data?.payroll_employee_name || data?.employee_name || data?.full_name)}</b></div>
                  <div><span>Employee ID</span><b>{safe(data?.employee_id || data?.payroll_employee)}</b></div>
                  <div><span>Salary Unit</span><b>{safe(data?.salary_unit)}</b></div>
                  <div><span>Payroll Source</span><b>{isBiometric ? "Biometric" : "Manual"}</b></div>
                </div>
              </div>

              <div className="salary-slip-card">
                <div className="salary-slip-card-title">Payment Details</div>
                <div className="salary-slip-info-table">
                  <div><span>Payment Method</span><b>{safe(data?.pay_method || data?.payment_method)}</b></div>
                  <div><span>Bank</span><b>{safe(data?.bank_name)}</b></div>
                  <div><span>Cheque No</span><b>{safe(data?.cheque_no)}</b></div>
                  <div><span>Status</span><b>Paid</b></div>
                </div>
              </div>
            </div>

            <div className="salary-slip-section-grid">
              <div className="salary-slip-card">
                <div className="salary-slip-card-title">Attendance & Hours</div>
                <div className="salary-slip-info-table">
                  <div><span>Total Hours</span><b>{safe(data?.total_hour, "0")} hrs</b></div>
                  <div><span>Allowed Daily Hours</span><b>{safe(data?.allowed_daily_hours)} hrs</b></div>
                  <div><span>Employee Leaves</span><b>{safe(data?.employee_leaves, "0")}</b></div>
                  <div><span>Paid Day Off</span><b>{safe(data?.paid_day_off_leaves, "0")}</b></div>
                </div>
              </div>

              <div className="salary-slip-card">
                <div className="salary-slip-card-title">Rate Calculation</div>
                <div className="salary-slip-info-table">
                  <div><span>Base Amount</span><b>$ {formatAmount(data?.salary_amount)}</b></div>
                  <div><span>Hourly Rate</span><b>{data?.calculated_hourly_rate ? `$ ${formatAmount(data.calculated_hourly_rate)}` : "-"}</b></div>
                  <div><span>Daily Rate</span><b>{data?.calculated_daily_rate ? `$ ${formatAmount(data.calculated_daily_rate)}` : "-"}</b></div>
                  <div><span>Bi-Weekly Amount</span><b>{data?.amount_per_bi_week ? `$ ${formatAmount(data.amount_per_bi_week)}` : "-"}</b></div>
                </div>
              </div>
            </div>

            <div className="salary-slip-breakdown">
              <div className="salary-slip-table-wrap">
                <div className="salary-slip-card-title">Earnings</div>
                <table className="salary-slip-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Basis</th>
                      <th className="amount">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earningsRows.map((row, index) => (
                      <tr key={index}>
                        <td>{row.label}</td>
                        <td>{row.basis}</td>
                        <td className="amount">$ {formatAmount(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="2">Gross Earnings</td>
                      <td className="amount">$ {formatAmount(grossSalary + taxAmount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="salary-slip-table-wrap">
                <div className="salary-slip-card-title">Deductions</div>
                <table className="salary-slip-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Basis</th>
                      <th className="amount">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deductionRows.length > 0 ? (
                      deductionRows.map((row, index) => (
                        <tr key={index}>
                          <td>{row.label}</td>
                          <td>{row.basis}</td>
                          <td className="amount">$ {formatAmount(row.amount)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td>No deductions</td>
                        <td>-</td>
                        <td className="amount">$ 0.00</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="2">Total Deductions</td>
                      <td className="amount">$ {formatAmount(taxAmount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="salary-slip-net-section">
              <div>
                <span>Amount in words</span>
                <strong>{safe(data?.amount_in_words, "Generated payroll payment")}</strong>
              </div>

              <div className="salary-slip-net-box">
                <span>Net Payable Salary</span>
                <strong>$ {formatAmount(netPay)}</strong>
              </div>
            </div>

            {data?.note && (
              <div className="salary-slip-note">
                <strong>Note:</strong> {data.note}
              </div>
            )}

            <div className="salary-slip-footer">
              <div className="salary-slip-signature">
                <div className="signature-line" />
                <span>Employee Signature</span>
              </div>

              <div className="salary-slip-disclaimer">
                <strong>System Generated Salary Slip</strong>
                <p>
                  This salary slip is generated electronically from the payroll system and is valid without manual signature unless required by company policy.
                </p>
              </div>

              <div className="salary-slip-signature">
                <div className="signature-line" />
                <span>Authorized Signature</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .salary-slip-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.62);
          z-index: 99999;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 24px;
          overflow-y: auto;
        }

        .salary-slip-modal {
          width: min(1060px, 96vw);
          background: #f8fafc;
          border-radius: 20px;
          box-shadow: 0 24px 80px rgba(15, 23, 42, 0.28);
          overflow: hidden;
        }

        .salary-slip-toolbar {
          padding: 16px 20px;
          background: #ffffff;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }

        .salary-slip-toolbar h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 800;
          color: #111827;
        }

        .salary-slip-toolbar p {
          margin: 3px 0 0;
          font-size: 12px;
          color: #6b7280;
        }

        .salary-slip-toolbar-actions {
          display: flex;
          gap: 10px;
        }

        .salary-slip-btn {
          border: none;
          border-radius: 10px;
          padding: 10px 16px;
          font-weight: 800;
          cursor: pointer;
          font-size: 13px;
        }

        .salary-slip-btn.light {
          background: #f3f4f6;
          color: #111827;
        }

        .salary-slip-btn.dark {
          background: #111827;
          color: #ffffff;
        }

        .salary-slip-print-area {
          padding: 22px;
        }

        .salary-slip-document {
          background: #ffffff;
          color: #111827;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid #e5e7eb;
          box-shadow: 0 8px 30px rgba(15, 23, 42, 0.08);
          font-family: Arial, Helvetica, sans-serif;
        }

        .salary-slip-top-band {
          height: 10px;
          background: linear-gradient(90deg, #111827, #ffaf32, #16a34a);
        }

        .salary-slip-header {
          padding: 24px 28px 20px;
          display: flex;
          justify-content: space-between;
          gap: 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .salary-slip-company {
          display: flex;
          gap: 14px;
          align-items: flex-start;
          min-width: 0;
        }

        .salary-slip-logo,
        .salary-slip-logo-placeholder {
          width: 64px;
          height: 64px;
          border-radius: 16px;
          object-fit: contain;
          flex-shrink: 0;
        }

        .salary-slip-logo-placeholder {
          background: #111827;
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          font-weight: 900;
        }

        .salary-slip-company h1 {
          margin: 0;
          font-size: 23px;
          font-weight: 900;
          color: #111827;
        }

        .salary-slip-company p {
          margin: 4px 0 0;
          font-size: 12px;
          color: #4b5563;
          line-height: 1.45;
        }

        .salary-slip-title-box {
          text-align: right;
          flex-shrink: 0;
        }

        .salary-slip-title {
          font-size: 28px;
          font-weight: 900;
          letter-spacing: 0.04em;
          color: #111827;
        }

        .salary-slip-subtitle {
          margin-top: 4px;
          display: inline-block;
          padding: 5px 10px;
          border-radius: 999px;
          background: #fff7ed;
          color: #c2410c;
          font-size: 12px;
          font-weight: 800;
        }

        .salary-slip-id {
          margin-top: 10px;
          font-size: 12px;
          color: #6b7280;
          font-weight: 700;
        }

        .salary-slip-summary-row {
          margin: 18px 28px;
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px;
        }

        .salary-slip-summary-row > div {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          padding: 12px;
        }

        .salary-slip-summary-row span,
        .salary-slip-net-section span {
          display: block;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #6b7280;
          font-weight: 800;
          margin-bottom: 5px;
        }

        .salary-slip-summary-row strong {
          font-size: 13px;
          color: #111827;
          word-break: break-word;
        }

        .salary-slip-net-highlight {
          color: #15803d !important;
          font-size: 16px !important;
        }

        .salary-slip-section-grid {
          margin: 0 28px 14px;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
        }

        .salary-slip-card,
        .salary-slip-table-wrap {
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          background: #ffffff;
          overflow: hidden;
        }

        .salary-slip-card-title {
          padding: 11px 14px;
          background: #111827;
          color: #ffffff;
          font-size: 13px;
          font-weight: 900;
          letter-spacing: 0.02em;
        }

        .salary-slip-info-table {
          padding: 8px 14px 12px;
        }

        .salary-slip-info-table div {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding: 8px 0;
          border-bottom: 1px dashed #e5e7eb;
          font-size: 12px;
        }

        .salary-slip-info-table div:last-child {
          border-bottom: none;
        }

        .salary-slip-info-table span {
          color: #6b7280;
          font-weight: 700;
        }

        .salary-slip-info-table b {
          color: #111827;
          text-align: right;
        }

        .salary-slip-breakdown {
          margin: 0 28px 14px;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
        }

        .salary-slip-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }

        .salary-slip-table th {
          background: #f3f4f6;
          color: #374151;
          padding: 10px 12px;
          text-align: left;
          font-size: 11px;
          text-transform: uppercase;
        }

        .salary-slip-table td {
          padding: 10px 12px;
          border-bottom: 1px solid #f3f4f6;
          color: #111827;
        }

        .salary-slip-table tfoot td {
          background: #f9fafb;
          font-weight: 900;
        }

        .salary-slip-table .amount {
          text-align: right;
          white-space: nowrap;
        }

        .salary-slip-net-section {
          margin: 0 28px 16px;
          border-radius: 16px;
          background: linear-gradient(135deg, #111827, #1f2937);
          color: #ffffff;
          padding: 16px 18px;
          display: flex;
          justify-content: space-between;
          gap: 20px;
          align-items: center;
        }

        .salary-slip-net-section span {
          color: #d1d5db;
        }

        .salary-slip-net-section strong {
          color: #ffffff;
          font-size: 14px;
        }

        .salary-slip-net-box {
          text-align: right;
          flex-shrink: 0;
        }

        .salary-slip-net-box strong {
          font-size: 26px;
          color: #86efac;
        }

        .salary-slip-note {
          margin: 0 28px 16px;
          padding: 12px 14px;
          border-radius: 12px;
          background: #fffbeb;
          border: 1px solid #fde68a;
          color: #92400e;
          font-size: 12px;
        }

        .salary-slip-footer {
          padding: 24px 28px 26px;
          display: grid;
          grid-template-columns: 1fr 1.4fr 1fr;
          gap: 22px;
          align-items: end;
          border-top: 1px solid #e5e7eb;
        }

        .salary-slip-signature {
          text-align: center;
          font-size: 12px;
          color: #374151;
          font-weight: 800;
        }

        .signature-line {
          border-top: 1px solid #9ca3af;
          height: 24px;
        }

        .salary-slip-disclaimer {
          text-align: center;
          font-size: 11px;
          color: #6b7280;
          line-height: 1.4;
        }

        .salary-slip-disclaimer strong {
          color: #111827;
          display: block;
          margin-bottom: 4px;
        }

        .salary-slip-disclaimer p {
          margin: 0;
        }

        @media (max-width: 768px) {
          .salary-slip-header,
          .salary-slip-net-section {
            flex-direction: column;
            text-align: left;
          }

          .salary-slip-title-box,
          .salary-slip-net-box {
            text-align: left;
          }

          .salary-slip-summary-row,
          .salary-slip-section-grid,
          .salary-slip-breakdown,
          .salary-slip-footer {
            grid-template-columns: 1fr;
          }
        }

        @media print {
          body * {
            visibility: hidden !important;
          }

          .salary-slip-modal-overlay,
          .salary-slip-modal-overlay * {
            visibility: visible !important;
          }

          .no-print {
            display: none !important;
          }

          .salary-slip-modal-overlay {
            position: static !important;
            inset: auto !important;
            background: #ffffff !important;
            padding: 0 !important;
            overflow: visible !important;
          }

          .salary-slip-modal {
            width: 100% !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            background: #ffffff !important;
            overflow: visible !important;
          }

          .salary-slip-print-area {
            padding: 0 !important;
          }

          .salary-slip-document {
            box-shadow: none !important;
            border-radius: 0 !important;
            border: none !important;
          }

          @page {
            size: A4;
            margin: 10mm;
          }
        }
      `}</style>
    </div>
  );
};

export default ReciptPrint;
